// ---------------------------------------------------------------------------
// Case slider (Splide)
// ---------------------------------------------------------------------------
import Splide from '@splidejs/splide';

export function initCaseSlider() {
    const el = document.querySelector('[data-case-slider]');
    if (!el) return;

    new Splide(el, {
        type: 'loop',
        perPage: 1,
        perMove: 1,
        gap: 10,
        pagination: false,
        arrows: true,
        autoplay: true,
        interval: 5000,
        speed: 700,
        pauseOnHover: true,
        pauseOnFocus: true,
        drag: true,
        autoHeight: true,
        breakpoints: {
            1100: {
                autoHeight: true,
            },
            765: {
                autoHeight: true,
            },
        },
    }).mount();
}

// ---------------------------------------------------------------------------
// Case — animated wave dots background
// ---------------------------------------------------------------------------
export function initCaseWave() {
    const canvas = document.querySelector('[data-case-wave]');
    if (!canvas) return;

    const section = canvas.closest('.case');
    const ctx = canvas.getContext('2d');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!section || !ctx) return;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let time = 0;
    let frameId = null;

    const resize = () => {
        const rect = section.getBoundingClientRect();

        width = Math.max(300, Math.round(rect.width));
        height = Math.max(300, Math.round(rect.height));
        dpr = Math.min(window.devicePixelRatio || 1, 2);

        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);

        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const draw = () => {
        ctx.clearRect(0, 0, width, height);

        const isMobile = width < 768;

        const startY = isMobile ? height * 0.25 : height * 0.35;
        const rows = isMobile ? 20 : 26;
        const cols = Math.ceil(width / 30) + 10;

        const rowStep = isMobile ? 22 : 25;
        const colStep = isMobile ? 25 : 30;
        const centerX = width / 2;

        const points = [];

        for (let row = 0; row < rows; row += 1) {
            const depth = row / rows;
            const yBase = startY + row * rowStep;
            const scale = 0.65 + depth * 1.15;
            const alpha = 0.015 + depth * 0.08;
            const amplitude = (1 - depth) * (isMobile ? 25 : 45);

            const rowPoints = [];

            for (let col = -5; col < cols; col += 1) {
                const xRaw = col * colStep;
                const xCentered = xRaw - centerX;
                const x = centerX + xCentered * scale;

                const waveOne = Math.sin(col * 0.45 + row * 0.35 + time) * amplitude;
                const waveTwo = Math.cos(col * 0.25 + row * 0.45 + time * 0.60) * amplitude * 0.45;
                const y = yBase + waveOne + waveTwo;

                rowPoints.push({ x, y, alpha, depth });
            }

            points.push(rowPoints);
        }

        ctx.lineWidth = 1;

        points.forEach((rowPoints) => {
            for (let index = 0; index < rowPoints.length - 1; index += 1) {
                const point = rowPoints[index];
                const nextPoint = rowPoints[index + 1];

                ctx.beginPath();
                ctx.moveTo(point.x, point.y);
                ctx.lineTo(nextPoint.x, nextPoint.y);
                ctx.strokeStyle = `rgba(20, 20, 20, ${point.alpha})`;
                ctx.stroke();
            }
        });

        for (let row = 0; row < points.length - 1; row += 1) {
            const rowPoints = points[row];
            const nextRowPoints = points[row + 1];

            for (let col = 0; col < rowPoints.length; col += 1) {
                const point = rowPoints[col];
                const nextPoint = nextRowPoints[col];

                if (!nextPoint) continue;

                ctx.beginPath();
                ctx.moveTo(point.x, point.y);
                ctx.lineTo(nextPoint.x, nextPoint.y);
                ctx.strokeStyle = `rgba(20, 20, 20, ${point.alpha * 0.65})`;
                ctx.stroke();
            }
        }

        points.forEach((rowPoints) => {
            rowPoints.forEach((point) => {
                const radius = 1 + point.depth * 2;

                ctx.beginPath();
                ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(20, 20, 20, ${point.alpha + 0.025})`;
                ctx.fill();
            });
        });
    };

    const animate = () => {
        if (!reduceMotion.matches) {
            time += 0.015;
        }

        draw();

        if (!reduceMotion.matches) {
            frameId = window.requestAnimationFrame(animate);
        }
    };

    const start = () => {
        resize();
        draw();

        if (!reduceMotion.matches) {
            frameId = window.requestAnimationFrame(animate);
        }
    };

    const stop = () => {
        if (frameId) {
            window.cancelAnimationFrame(frameId);
            frameId = null;
        }
    };

    window.addEventListener('resize', () => {
        stop();
        start();
    }, { passive: true });

    reduceMotion.addEventListener?.('change', () => {
        stop();
        start();
    });

    start();
}